// Мок ИИ-загрузки каталога (BUN_PUBLIC_MOCK_DOMAINS=catalogImport): мастер
// загрузки собирается и проверяется без шлюза. Всё живёт в памяти вкладки —
// перезагрузка страницы возвращает исходный набор заданий.
//
// Что изображает:
// - задания во всех статусах: материалы, разбор идёт, проверка на 40 позиций,
//   загрузка идёт, загружено с ошибками, сбой разбора, отменено;
// - файлы: вид по расширению, архив «распаковывается» в шесть картинок, файл
//   со словом «сбой»/«fail» в имени падает на первой попытке — виден повтор;
// - разбор и загрузка идут по часам: этап и done/total считаются от момента
//   запуска при каждом опросе, расход токенов и $ растёт вместе с ними;
// - картинки рисуются на canvas (PNG), PDF — минимальный настоящий файл.
// Подсказка модели со словом «сбой» роняет разбор на разметке — так
// проверяется экран ошибки задания.
import { flattenCategoryTreeV2 } from "@osmix/catalog-core/category-tree";
import {
	CATALOG_IMPORT_FILE_MAX_BYTES,
	CATALOG_IMPORT_MAX_ITEMS,
	type CatalogImportFile,
	type CatalogImportFileKind,
	type CatalogImportFileStatus,
	type CatalogImportIssue,
	type CatalogImportItem,
	type CatalogImportItemDraft,
	type CatalogImportJob,
	type CatalogImportJobDetail,
	type CatalogImportModel,
	type CatalogImportOptions,
	type CatalogImportProgress,
	type CatalogImportShopDraft,
	type CatalogImportStage,
	type CatalogImportStats,
	type CatalogImportTarget,
	type CatalogImportUsage,
} from "@osmix/contracts";
import { sleep } from "@/lib/utils";
import { ApiError } from "../errors";
import type { Api } from "../types";

// ── Справочники ──────────────────────────────────────────────────────────

const MODELS: CatalogImportModel[] = [
	{
		id: "google/gemini-2.5-flash",
		label: "Gemini 2.5 Flash",
		vision: true,
		priceIn: 0.3,
		priceOut: 2.5,
		note: "Быстро и недорого, читает фото и сканы PDF",
	},
	{
		id: "anthropic/claude-sonnet-4.5",
		label: "Claude Sonnet 4.5",
		vision: true,
		priceIn: 3,
		priceOut: 15,
		note: "Точнее на сложных каталогах и грязных прайсах, дороже в 6–10 раз",
	},
	{
		id: "openai/gpt-4.1-mini",
		label: "GPT-4.1 mini",
		vision: true,
		priceIn: 0.4,
		priceOut: 1.6,
	},
	{
		id: "deepseek/deepseek-chat-v3.1",
		label: "DeepSeek V3.1",
		vision: false,
		priceIn: 0.27,
		priceOut: 1.1,
		note: "Только текст: прайсы и таблицы, фото не видит",
	},
];
const DEFAULT_MODEL = "google/gemini-2.5-flash";

const DEFAULT_OPTIONS: CatalogImportOptions = {
	publishMode: "draft",
	rewriteDescriptions: true,
	matchPhotosByVision: true,
	fillAttributes: true,
};

const ZERO_USAGE: CatalogImportUsage = { calls: 0, tokensIn: 0, tokensOut: 0, costUsd: 0 };
const IDLE: CatalogImportProgress = { stage: null, done: 0, total: 0, message: "" };

/** Пути категорий — из того же дерева v2, что у шлюза и мока категорий. */
const CATEGORY_PATHS = (() => {
	const nodes = flattenCategoryTreeV2();
	const byId = new Map(nodes.map((n) => [n.id, n]));
	const out = new Map<string, string>();
	for (const node of nodes) {
		const names: string[] = [];
		for (let cur: typeof node | undefined = node; cur; cur = cur.parentId ? byId.get(cur.parentId) : undefined) {
			names.unshift(cur.name);
		}
		out.set(node.id, names.join(" / "));
	}
	return out;
})();

// ── Случайность с семенем: одни и те же задания при каждой загрузке ─────

const hash = (s: string): number => {
	let h = 2166136261;
	for (const ch of s) {
		h ^= ch.codePointAt(0) ?? 0;
		h = Math.imul(h, 16777619);
	}
	return h >>> 0;
};

const seeded = (seed: string) => {
	let a = hash(seed);
	return () => {
		a = (a + 0x6d2b79f5) | 0;
		let t = Math.imul(a ^ (a >>> 15), 1 | a);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
};

let counter = 0;
const mintId = (prefix: string) => `${prefix}_${Date.now().toString(36)}${(counter++).toString(36)}`;

const BOOT = Date.now();
const ago = (minutes: number) => new Date(BOOT - minutes * 60_000).toISOString();
const nowIso = () => new Date().toISOString();

// ── Картинки и файлы ─────────────────────────────────────────────────────

type Shape =
	| "dress"
	| "shirt"
	| "pants"
	| "skirt"
	| "bag"
	| "scarf"
	| "belt"
	| "hat"
	| "ring"
	| "textile"
	| "ceramic"
	| "logo"
	| "cover";

const COLORS: Record<string, string> = {
	белый: "#f2f0ea",
	молочный: "#ece2cc",
	бежевый: "#d8c3a1",
	натуральный: "#cbb894",
	оливковый: "#7d8656",
	голубой: "#9fbbd3",
	синий: "#3f5c82",
	терракотовый: "#c2714f",
	лавандовый: "#b4a6cf",
	чёрный: "#2c2c2c",
	графит: "#4b4e54",
	серебро: "#b9bcc2",
	золото: "#d4b25a",
	серый: "#a3a3a0",
	розовый: "#e3b6b8",
};

type FileSource =
	| { kind: "upload"; blob: Blob }
	| { kind: "image"; shape: Shape; color: string; label: string }
	| { kind: "text"; text: string; mime: string }
	| { kind: "pdf"; lines: string[] };

const KIND_BY_EXT: Record<string, CatalogImportFileKind> = {
	xlsx: "spreadsheet",
	xls: "spreadsheet",
	xlsm: "spreadsheet",
	csv: "spreadsheet",
	tsv: "spreadsheet",
	ods: "spreadsheet",
	numbers: "spreadsheet",
	doc: "document",
	docx: "document",
	rtf: "document",
	odt: "document",
	pages: "document",
	pdf: "pdf",
	jpg: "image",
	jpeg: "image",
	png: "image",
	webp: "image",
	gif: "image",
	heic: "image",
	heif: "image",
	avif: "image",
	bmp: "image",
	tif: "image",
	tiff: "image",
	txt: "text",
	md: "text",
	json: "text",
	html: "text",
	htm: "text",
	eml: "text",
	zip: "archive",
	rar: "archive",
	"7z": "archive",
};

const MIME_BY_KIND: Record<CatalogImportFileKind, string> = {
	spreadsheet: "text/csv",
	document: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	pdf: "application/pdf",
	image: "image/png",
	text: "text/plain",
	archive: "application/zip",
	other: "application/octet-stream",
};

const extOf = (name: string) => name.toLowerCase().split(".").pop() ?? "";
const kindOf = (name: string, mime: string): CatalogImportFileKind => {
	const byExt = KIND_BY_EXT[extOf(name)];
	if (byExt) return byExt;
	if (mime.startsWith("image/")) return "image";
	if (mime.startsWith("text/")) return "text";
	return "other";
};

/** Сколько байт у строки в UTF-8 — размер «файла» вставленного текста. */
const byteLength = (text: string) => new TextEncoder().encode(text).length;

const lighten = (hex: string, amount: number) => {
	const n = Number.parseInt(hex.slice(1), 16);
	const mix = (c: number) => Math.round(amount < 0 ? c * (1 + amount) : c + (255 - c) * amount);
	const r = mix((n >> 16) & 255);
	const g = mix((n >> 8) & 255);
	const b = mix(n & 255);
	return `rgb(${r}, ${g}, ${b})`;
};

/** Силуэт вещи на нейтральном фоне — «фото товара» без внешних картинок. */
const drawImage = async (shape: Shape, color: string, label: string): Promise<Blob> => {
	const w = 480;
	const h = 600;
	const canvas = document.createElement("canvas");
	canvas.width = w;
	canvas.height = h;
	const ctx = canvas.getContext("2d");
	if (!ctx) return new Blob([], { type: "image/png" });
	ctx.fillStyle = shape === "cover" ? lighten(color, 0.55) : "#f3f2ef";
	ctx.fillRect(0, 0, w, h);
	ctx.fillStyle = color;
	ctx.strokeStyle = lighten(color, -0.3);
	ctx.lineWidth = 4;
	ctx.lineJoin = "round";
	ctx.beginPath();
	const p = (pts: [number, number][]) => {
		pts.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x * w, y * h) : ctx.lineTo(x * w, y * h)));
		ctx.closePath();
	};
	switch (shape) {
		case "dress":
			p([
				[0.4, 0.12],
				[0.6, 0.12],
				[0.62, 0.34],
				[0.8, 0.86],
				[0.2, 0.86],
				[0.38, 0.34],
			]);
			break;
		case "shirt":
			p([
				[0.36, 0.16],
				[0.44, 0.13],
				[0.5, 0.18],
				[0.56, 0.13],
				[0.64, 0.16],
				[0.86, 0.34],
				[0.78, 0.42],
				[0.68, 0.34],
				[0.68, 0.84],
				[0.32, 0.84],
				[0.32, 0.34],
				[0.22, 0.42],
				[0.14, 0.34],
			]);
			break;
		case "pants":
			p([
				[0.32, 0.14],
				[0.68, 0.14],
				[0.72, 0.88],
				[0.55, 0.88],
				[0.5, 0.34],
				[0.45, 0.88],
				[0.28, 0.88],
			]);
			break;
		case "skirt":
			p([
				[0.36, 0.22],
				[0.64, 0.22],
				[0.8, 0.78],
				[0.2, 0.78],
			]);
			break;
		case "bag":
			ctx.roundRect(0.22 * w, 0.36 * h, 0.56 * w, 0.44 * h, 18);
			break;
		case "scarf":
			ctx.moveTo(0.18 * w, 0.2 * h);
			ctx.bezierCurveTo(0.6 * w, 0.1 * h, 0.4 * w, 0.5 * h, 0.82 * w, 0.42 * h);
			ctx.lineTo(0.82 * w, 0.58 * h);
			ctx.bezierCurveTo(0.4 * w, 0.66 * h, 0.6 * w, 0.26 * h, 0.18 * w, 0.36 * h);
			ctx.closePath();
			break;
		case "belt":
			ctx.rect(0.08 * w, 0.46 * h, 0.84 * w, 0.08 * h);
			break;
		case "hat":
			ctx.ellipse(0.5 * w, 0.58 * h, 0.36 * w, 0.08 * h, 0, 0, Math.PI * 2);
			ctx.moveTo(0.3 * w, 0.56 * h);
			ctx.bezierCurveTo(0.3 * w, 0.3 * h, 0.7 * w, 0.3 * h, 0.7 * w, 0.56 * h);
			break;
		case "ring":
			ctx.lineWidth = 26;
			ctx.strokeStyle = color;
			ctx.arc(0.5 * w, 0.48 * h, 0.2 * w, 0, Math.PI * 2);
			ctx.stroke();
			ctx.beginPath();
			ctx.fillStyle = "#e9eef5";
			ctx.arc(0.5 * w, 0.48 * h - 0.2 * w, 22, 0, Math.PI * 2);
			break;
		case "textile":
			for (let i = 0; i < 3; i++) ctx.rect(0.2 * w, (0.3 + i * 0.14) * h, 0.6 * w, 0.12 * h);
			break;
		case "ceramic":
			p([
				[0.38, 0.22],
				[0.62, 0.22],
				[0.6, 0.3],
				[0.72, 0.5],
				[0.66, 0.8],
				[0.34, 0.8],
				[0.28, 0.5],
				[0.4, 0.3],
			]);
			break;
		case "logo":
			ctx.arc(0.5 * w, 0.45 * h, 0.3 * w, 0, Math.PI * 2);
			break;
		case "cover":
			ctx.rect(0, 0.62 * h, w, 0.38 * h);
			break;
	}
	ctx.fill();
	if (shape !== "ring") ctx.stroke();
	if (shape === "belt") {
		ctx.strokeStyle = "#8d7a52";
		ctx.lineWidth = 8;
		ctx.strokeRect(0.66 * w, 0.43 * h, 0.1 * w, 0.14 * h);
	}
	if (shape === "logo") {
		ctx.fillStyle = "#ffffff";
		ctx.font = "600 96px system-ui, sans-serif";
		ctx.textAlign = "center";
		ctx.fillText("ЛХ", 0.5 * w, 0.45 * h + 34);
	}
	ctx.fillStyle = "#8b8b86";
	ctx.font = "18px system-ui, sans-serif";
	ctx.textAlign = "left";
	ctx.fillText(label.length > 44 ? `${label.slice(0, 43)}…` : label, 22, h - 24);
	return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob ?? new Blob([], { type: "image/png" })), "image/png"));
};

/** Настоящий одностраничный PDF (латиница — шрифт Helvetica без кириллицы). */
const makePdf = (lines: string[]): Blob => {
	const text = lines
		.map((line, i) => `BT /F1 ${i === 0 ? 22 : 13} Tf 56 ${780 - i * 28} Td (${line.replace(/[()\\]/g, "")}) Tj ET`)
		.join("\n");
	const objects = [
		"<< /Type /Catalog /Pages 2 0 R >>",
		"<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
		"<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>",
		`<< /Length ${text.length} >>\nstream\n${text}\nendstream`,
		"<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
	];
	let body = "%PDF-1.4\n";
	const offsets: number[] = [];
	objects.forEach((obj, i) => {
		offsets.push(body.length);
		body += `${i + 1} 0 obj\n${obj}\nendobj\n`;
	});
	const xref = body.length;
	body += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
	for (const offset of offsets) body += `${String(offset).padStart(10, "0")} 00000 n \n`;
	body += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
	return new Blob([body], { type: "application/pdf" });
};

// ── Позиции ──────────────────────────────────────────────────────────────

type Proto = {
	name: string;
	code: string;
	cat: string | null;
	hint?: string;
	shape: Shape;
	colors: string[];
	sizes: string[];
	price: number;
	old?: number;
	comp?: string;
	desc: string;
	chars?: Record<string, string>;
	keywords?: string[];
};

const W = ["42", "44", "46", "48", "50"];
const M = ["46", "48", "50", "52", "54"];

/** Летняя коллекция ателье «Лён и Хлопок» — главный пример проверки. */
const LINEN: Proto[] = [
	{ name: "Платье «Ривьера» льняное миди", code: "PL-01", cat: "cat-1-1", shape: "dress", colors: ["молочный", "оливковый"], sizes: W, price: 6490, old: 7490, comp: "Лён 100%", desc: "Свободное платье миди с коротким рукавом и карманами в боковых швах. Лён ивановской фабрики, мягкая стирка — ткань не колется с первого дня.", chars: { Длина: "Миди", Силуэт: "Свободный", Рукав: "Короткий" }, keywords: ["льняное платье", "платье миди"] },
	{ name: "Платье-рубашка «Прованс»", code: "PL-02", cat: "cat-1-1", shape: "dress", colors: ["белый", "голубой"], sizes: W, price: 5990, comp: "Лён 100%", desc: "Платье-рубашка на пуговицах с поясом в комплекте. Носится застёгнутым и как лёгкий жакет поверх майки.", chars: { Длина: "Ниже колена", Застёжка: "Пуговицы" } },
	{ name: "Платье «Лаванда» с запахом", code: "PL-03", cat: "cat-1-1", shape: "dress", colors: ["лавандовый"], sizes: W, price: 6890, comp: "Лён 70%, хлопок 30%", desc: "Платье на запах с V-вырезом и рукавом три четверти. Запах регулируется — садится и на 42, и на 46.", chars: { Силуэт: "На запах", Рукав: "Три четверти" } },
	{ name: "Платье макси «Сиена»", code: "PL-04", cat: "cat-1-1", shape: "dress", colors: ["терракотовый", "чёрный"], sizes: W, price: 7490, old: 8990, comp: "Лён 100%", desc: "Длинное платье в пол на тонких бретелях с открытой спиной. Подкладка из хлопкового батиста.", chars: { Длина: "Макси" } },
	{ name: "Платье «Минори» на бретелях", code: "PL-05", cat: "cat-1-1", shape: "dress", colors: ["бежевый"], sizes: W, price: 4990, comp: "Лён 100%", desc: "Короткое платье-комбинация на регулируемых бретелях. Хорошо носится с рубашкой «Бриз» сверху." },
	{ name: "Платье-трапеция «Ока»", code: "PL-06", cat: "cat-1-1", shape: "dress", colors: ["синий"], sizes: W, price: 5490, comp: "Лён 100%", desc: "Платье трапециевидного силуэта с круглым вырезом и рукавом-фонариком." },
	{ name: "Сарафан «Полдень» льняной", code: "SR-01", cat: "cat-1-1", shape: "dress", colors: ["молочный", "оливковый"], sizes: W, price: 4790, comp: "Лён 100%", desc: "Сарафан на широких бретелях с квадратным вырезом и сборкой по спинке." },
	{ name: "Сарафан «Дача» в полоску", code: "SR-02", cat: "cat-1-1", shape: "dress", colors: ["голубой"], sizes: W, price: 4290, comp: "Лён 55%, хлопок 45%", desc: "Сарафан в тонкую голубую полоску с карманами и поясом на завязках." },
	{ name: "Рубашка оверсайз «Бриз»", code: "RB-01", cat: "cat-1-3", shape: "shirt", colors: ["белый", "бежевый", "голубой"], sizes: W, price: 4590, comp: "Лён 100%", desc: "Рубашка свободного кроя со спущенным плечом и удлинённой спинкой.", chars: { Крой: "Оверсайз" } },
	{ name: "Рубашка «Фьорд» с карманами", code: "RB-02", cat: "cat-1-3", shape: "shirt", colors: ["оливковый"], sizes: W, price: 4890, comp: "Лён 100%", desc: "Рубашка с двумя накладными карманами и хлястиками на рукавах." },
	{ name: "Рубашка мужская «Кавказ» льняная", code: "RM-01", cat: "cat-2-1", shape: "shirt", colors: ["белый", "синий"], sizes: M, price: 5290, comp: "Лён 100%", desc: "Мужская рубашка с воротником-стойкой и длинным рукавом. Плотный лён, не просвечивает.", chars: { Воротник: "Стойка" } },
	{ name: "Рубашка мужская «Гавана» с коротким рукавом", code: "RM-02", cat: "cat-2-1", shape: "shirt", colors: ["бежевый"], sizes: M, price: 4690, comp: "Лён 100%", desc: "Летняя рубашка с отложным воротником и коротким рукавом." },
	{ name: "Блуза «Нежность» с вышивкой", code: "BL-01", cat: "cat-1-3", shape: "shirt", colors: ["молочный"], sizes: W, price: 5190, comp: "Хлопок 100%", desc: "Блуза из хлопкового батиста с ручной вышивкой гладью по горловине." },
	{ name: "Блуза «Ирис» без рукавов", code: "BL-02", cat: "cat-1-3", shape: "shirt", colors: ["голубой"], sizes: W, price: 3790, comp: "Лён 100%", desc: "Лёгкая блуза без рукавов с застёжкой на спинке." },
	{ name: "Блуза «Кружево» хлопковая", code: "BL-03", cat: "cat-1-3", shape: "shirt", colors: ["белый"], sizes: W, price: 4390, comp: "Хлопок 100%", desc: "" },
	{ name: "Брюки палаццо «Ветер»", code: "BR-01", cat: "cat-w-pants", shape: "pants", colors: ["молочный", "чёрный"], sizes: W, price: 4990, comp: "Лён 100%", desc: "Широкие брюки с высокой посадкой и мягкими складками от пояса.", chars: { Посадка: "Высокая", Силуэт: "Палаццо" } },
	{ name: "Брюки на резинке «Уют»", code: "BR-02", cat: "cat-w-pants", shape: "pants", colors: ["оливковый"], sizes: W, price: 4490, comp: "Лён 100%", desc: "Прямые брюки на широкой резинке с кулиской." },
	{ name: "Брюки-кюлоты «Тоскана»", code: "BR-03", cat: "cat-w-pants", shape: "pants", colors: ["терракотовый"], sizes: W, price: 4790, comp: "Лён 100%", desc: "Укороченные широкие брюки до середины икры." },
	{ name: "Брюки мужские льняные «Марсель»", code: "BM-01", cat: "cat-2-2", shape: "pants", colors: ["бежевый", "графит"], sizes: M, price: 5490, comp: "Лён 100%", desc: "Мужские брюки прямого кроя с поясом на пуговице и шнурком." },
	{ name: "Юбка миди «Волна» на запах", code: "UB-01", cat: "cat-1-2", shape: "skirt", colors: ["оливковый"], sizes: W, price: 4290, comp: "Лён 100%", desc: "Юбка на запах длиной миди с завязками на талии." },
	{ name: "Юбка-трапеция «Лето»", code: "UB-02", cat: "cat-1-2", shape: "skirt", colors: ["белый"], sizes: W, price: 3890, comp: "Лён 100%", desc: "Юбка А-силуэта до колена с потайной молнией." },
	{ name: "Юбка макси «Ярмарка» ярусная", code: "UB-03", cat: "cat-1-2", shape: "skirt", colors: ["терракотовый"], sizes: W, price: 4990, comp: "Хлопок 100%", desc: "Длинная ярусная юбка из хлопкового муслина." },
	{ name: "Шорты «Пляж» с поясом", code: "SH-01", cat: "cat-w-pants", shape: "pants", colors: ["бежевый"], sizes: W, price: 2990, comp: "Лён 100%", desc: "Короткие шорты с высокой посадкой и поясом-кушаком." },
	{ name: "Шорты-бермуды «Город»", code: "SH-02", cat: "cat-w-pants", shape: "pants", colors: ["графит"], sizes: W, price: 3290, comp: "Лён 100%", desc: "Бермуды до колена со стрелками и отворотами." },
	{ name: "Костюм «Капри»: рубашка и шорты", code: "KS-01", cat: "cat-1-3", hint: "Костюмы", shape: "shirt", colors: ["молочный"], sizes: W, price: 7490, comp: "Лён 100%", desc: "Комплект из рубашки свободного кроя и шорт на резинке." },
	{ name: "Костюм «Портофино»: жакет и брюки", code: "KS-02", cat: "cat-1-4", hint: "Костюмы", shape: "shirt", colors: ["бежевый"], sizes: W, price: 12900, comp: "Лён 100%", desc: "Двубортный жакет и прямые брюки из плотного льна." },
	{ name: "Жакет льняной «Милан»", code: "ZH-01", cat: "cat-1-4", shape: "shirt", colors: ["бежевый", "чёрный"], sizes: W, price: 7990, comp: "Лён 100%", desc: "Однобортный жакет без подкладки с накладными карманами." },
	{ name: "Жилет «Рим» удлинённый", code: "ZH-02", cat: "cat-1-4", shape: "shirt", colors: ["оливковый"], sizes: W, price: 4590, comp: "Лён 100%", desc: "Удлинённый жилет на пуговицах, носится с поясом и без." },
	{ name: "Кардиган хлопковый «Утро»", code: "KR-01", cat: "cat-w-knit", shape: "shirt", colors: ["молочный"], sizes: W, price: 5690, comp: "Хлопок 100%", desc: "Кардиган тонкой вязки на перламутровых пуговицах." },
	{ name: "Джемпер льняной «Север» тонкой вязки", code: "KR-02", cat: "cat-w-knit", shape: "shirt", colors: ["синий"], sizes: W, price: 5290, comp: "Лён 60%, хлопок 40%", desc: "Джемпер из льняной пряжи с круглым вырезом." },
	{ name: "Футболка льняная базовая", code: "FT-01", cat: "cat-w-tshirts", shape: "shirt", colors: ["белый", "чёрный", "бежевый"], sizes: W, price: 2490, comp: "Лён 100%", desc: "Базовая футболка из льняного трикотажа прямого кроя." },
	{ name: "Топ «Лиана» на завязках", code: "FT-02", cat: "cat-w-tshirts", shape: "shirt", colors: ["терракотовый"], sizes: W, price: 2290, comp: "Лён 100%", desc: "Короткий топ с завязками на плечах." },
	{ name: "Лонгслив хлопковый «Дом»", code: "FT-03", cat: "cat-w-tshirts", shape: "shirt", colors: ["молочный"], sizes: W, price: 2790, comp: "Хлопок 100%", desc: "Лонгслив из мягкого хлопка с манжетами." },
	{ name: "Шопер льняной «Рынок»", code: "SM-01", cat: "cat-w-bags", shape: "bag", colors: ["натуральный"], sizes: [], price: 1890, comp: "Лён 100%", desc: "Вместительный шопер с длинными ручками и внутренним карманом.", chars: { Размер: "40 × 38 см" } },
	{ name: "Сумка через плечо «Пикник»", code: "SM-02", cat: "cat-w-bags", shape: "bag", colors: ["бежевый"], sizes: [], price: 3490, comp: "Лён 100%, кожа", desc: "Небольшая сумка с кожаным ремнём и магнитной застёжкой." },
	{ name: "Косметичка льняная", code: "SM-03", cat: null, hint: "Косметички", shape: "bag", colors: ["оливковый"], sizes: [], price: 990, comp: "Лён 100%", desc: "Косметичка на молнии с водоотталкивающей подкладкой." },
	{ name: "Палантин льняной «Туман»", code: "AK-01", cat: "cat-w-hats", shape: "scarf", colors: ["молочный", "голубой"], sizes: [], price: 2190, comp: "Лён 100%", desc: "Лёгкий палантин 70 × 180 см с бахромой." },
	{ name: "Платок хлопковый «Сад»", code: "AK-02", cat: "cat-w-hats", shape: "scarf", colors: ["терракотовый"], sizes: [], price: 1490, comp: "Хлопок 100%", desc: "Квадратный платок 90 × 90 см с цветочным принтом." },
	{ name: "Пояс-кушак льняной", code: "AK-03", cat: "cat-w-acc-other", shape: "belt", colors: ["бежевый"], sizes: [], price: 1290, comp: "Лён 100%", desc: "Широкий пояс-кушак на завязках." },
	{ name: "Панама льняная «Солнце»", code: "AK-04", cat: "cat-w-hats", shape: "hat", colors: ["молочный"], sizes: ["55-56", "57-58"], price: 1690, comp: "Лён 100%", desc: "Панама с мягкими полями, складывается в сумку." },
];

const JEWELRY: Proto[] = [
	["Серьги-кольца «Луна» серебро 925", "SE-01", [], 3490],
	["Кольцо «Волна» с фианитом", "KO-01", ["16", "16.5", "17", "17.5", "18"], 2890],
	["Подвеска «Капля» с топазом", "PO-01", [], 4290],
	["Браслет-цепочка «Нить»", "BR-01", ["16", "18"], 2490],
	["Серьги-пусеты «Звезда»", "SE-02", [], 1990],
	["Кольцо-печатка «Север»", "KO-02", ["17", "18", "19", "20"], 5490],
	["Колье «Лунный свет»", "KL-01", [], 8990],
	["Браслет «Ракушки»", "BR-02", ["16", "18"], 3290],
	["Серьги «Ветка» с позолотой", "SE-03", [], 3990],
	["Кольцо «Шторм» с чернением", "KO-03", ["16", "17", "18"], 3190],
	["Подвеска «Ключ»", "PO-02", [], 2690],
	["Цепочка «Якорная»", "CE-01", ["40", "45", "50"], 2990],
	["Серьги-каффы «Перо»", "SE-04", [], 2790],
	["Кольцо «Дуэт» парное", "KO-04", ["16", "17", "18", "19"], 6490],
].map(([name, code, sizes, price]) => ({
	name: name as string,
	code: code as string,
	cat: "cat-w-jewelry",
	shape: "ring" as const,
	colors: (code as string).startsWith("SE-03") ? ["золото"] : ["серебро"],
	sizes: sizes as string[],
	price: price as number,
	comp: "Серебро 925",
	desc: "Украшение из серебра 925 пробы с родиевым покрытием — не темнеет и не оставляет следов на коже.",
	chars: { Металл: "Серебро 925", Покрытие: "Родий" },
}));

const TEXTILE: Proto[] = [
	"Комплект постельного белья «Лён» 1,5-сп.",
	"Комплект постельного белья «Лён» 2-сп.",
	"Комплект постельного белья «Лён» евро",
	"Простыня льняная на резинке 160×200",
	"Наволочки льняные 50×70, 2 шт.",
	"Плед вафельный «Тёплый вечер»",
	"Полотенце вафельное 45×70",
	"Полотенце банное льняное 70×140",
	"Скатерть льняная 140×180",
	"Салфетки льняные, 4 шт.",
	"Фартук льняной «Кухня»",
	"Прихватки льняные, 2 шт.",
	"Покрывало «Сота» 200×220",
	"Наматрасник хлопковый 160×200",
	"Подушка декоративная «Лён» 45×45",
	"Чехол на подушку 45×45",
	"Халат вафельный",
	"Полотенце для рук 30×50",
	"Дорожка на стол 40×150",
	"Мешочек для хлеба",
	"Шторы льняные 150×270",
	"Плед муслиновый",
	"Простыня хлопковая 220×240",
	"Набор полотенец, 3 шт.",
].map((name, i) => ({
	name,
	code: String(i + 1).padStart(3, "0"),
	cat: "cat-7-2",
	shape: "textile" as const,
	colors: [["натуральный", "серый", "белый", "оливковый"][i % 4] as string],
	sizes: [],
	price: 890 + ((i * 731) % 7000),
	comp: i % 3 === 0 ? "Лён 100%" : "Лён 50%, хлопок 50%",
	desc: "Домашний текстиль из умягчённого льна: после стирки становится только мягче.",
}));

const CERAMICS: Proto[] = [
	"Кружка «Глина» 350 мл",
	"Кружка «Море» 300 мл",
	"Тарелка обеденная «Песок»",
	"Тарелка десертная «Песок»",
	"Пиала «Рисовая»",
	"Ваза «Амфора» малая",
	"Ваза «Амфора» большая",
	"Кашпо «Камень»",
	"Подсвечник «Луч»",
	"Салатник «Лес»",
	"Чайник заварочный «Утро»",
	"Сахарница «Кубик»",
	"Блюдо сервировочное «Дуб»",
	"Набор пиал, 4 шт.",
	"Молочник «Корова»",
	"Подставка под горячее",
	"Ложка керамическая",
	"Масленка «Ферма»",
	"Тарелка «Ракушка»",
	"Кружка «Облако» 400 мл",
].map((name, i) => ({
	name,
	code: String(i + 1).padStart(2, "0"),
	cat: i === 5 || i === 6 || i === 8 ? "cat-7-3" : "cat-7-1",
	shape: "ceramic" as const,
	colors: [["бежевый", "голубой", "серый", "терракотовый"][i % 4] as string],
	sizes: [],
	price: 790 + ((i * 397) % 3200),
	comp: "Керамика, глазурь",
	desc: "Посуда ручной работы: формовка на гончарном круге, обжиг дважды. Можно в посудомойку.",
}));

const FAIL_MESSAGES = [
	"Фото не скачалось: сервер магазина ответил 404",
	"Сервис каталога отклонил цену: ниже минимальной для категории",
	"Размер не подходит к сетке категории — выберите размер из сетки",
];

type Quirk =
	| "noPhoto"
	| "lowConfidence"
	| "noPrice"
	| "somePriceMissing"
	| "noDescription"
	| "excluded"
	| "photoUrl";

type BuildContext = {
	jobId: string;
	brand: string;
	prefix: string;
	/** Картинки задания по позициям (позиция → id файлов). */
	photos: Map<number, string[]>;
	sourceFileId: string | null;
	pdfFileId?: string | null;
	quirks?: Map<number, Quirk[]>;
	options: CatalogImportOptions;
	seed: string;
};

const barcode = (rnd: () => number) =>
	`46${Array.from({ length: 11 }, () => Math.floor(rnd() * 10)).join("")}`;

const buildItem = (proto: Proto, position: number, ctx: BuildContext): CatalogImportItem => {
	const rnd = seeded(`${ctx.seed}:${position}`);
	const quirks = new Set(ctx.quirks?.get(position) ?? []);
	const sizes = proto.sizes.length ? proto.sizes : [""];
	const variants = proto.colors.flatMap((color) =>
		sizes.map((size, si) => ({
			size,
			color,
			price: quirks.has("noPrice") || (quirks.has("somePriceMissing") && si === sizes.length - 1) ? null : proto.price,
			oldPrice: proto.old ?? null,
			stock: rnd() < 0.12 ? null : Math.floor(rnd() * 14),
			barcode: rnd() < 0.7 ? barcode(rnd) : undefined,
		})),
	);
	const photoFileIds = quirks.has("noPhoto") || quirks.has("photoUrl") ? [] : (ctx.photos.get(position) ?? []);
	const sourceRefs: CatalogImportItemDraft["sourceRefs"] = [];
	if (ctx.sourceFileId) sourceRefs.push({ fileId: ctx.sourceFileId, locator: `строка ${position * 2 + 2}` });
	if (ctx.pdfFileId && position % 3 === 0) sourceRefs.push({ fileId: ctx.pdfFileId, locator: `стр. ${Math.floor(position / 4) + 2}` });
	const data: CatalogImportItemDraft = {
		name: proto.name,
		article: `${ctx.prefix}-${proto.code}`,
		brand: ctx.brand,
		categoryId: proto.cat,
		categoryPath: proto.cat ? CATEGORY_PATHS.get(proto.cat) : undefined,
		categoryHint: proto.hint,
		color: proto.colors.join(", "),
		description: quirks.has("noDescription") || !proto.desc ? "" : proto.desc,
		composition: proto.comp,
		characteristics: {
			...(proto.comp ? { Состав: proto.comp } : {}),
			...proto.chars,
			"Страна производства": "Россия",
		},
		tags: proto.comp?.includes("Лён") ? ["лён", "натуральные ткани"] : [],
		keywords: proto.keywords ?? [proto.name.split(" ")[0]?.toLowerCase() ?? "", ...proto.colors],
		variants,
		photoFileIds,
		photoUrls: quirks.has("photoUrl") ? [`https://cdn.example.ru/${ctx.prefix.toLowerCase()}/${proto.code.toLowerCase()}.jpg`] : [],
		sourceRefs,
		confidence: quirks.has("lowConfidence") ? 0.48 + rnd() * 0.1 : 0.82 + rnd() * 0.17,
	};
	const item: CatalogImportItem = {
		id: `${ctx.jobId}_it${position + 1}`,
		jobId: ctx.jobId,
		position: position + 1,
		status: "ready",
		data,
		issues: [],
		productId: null,
		existingProductId: null,
		updatedAt: nowIso(),
	};
	return revalidate(item, ctx.options, quirks.has("excluded"));
};

/** Проблемы позиции по её данным — как их посчитал бы шлюз после правки. */
const issuesOf = (data: CatalogImportItemDraft, options: CatalogImportOptions): CatalogImportIssue[] => {
	const issues: CatalogImportIssue[] = [];
	if (!data.name.trim()) issues.push({ field: "name", level: "error", message: "Нет названия" });
	if (data.variants.length === 0) {
		issues.push({ field: "variants", level: "error", message: "Нет ни одного варианта с ценой" });
	} else {
		const missing = data.variants.findIndex((v) => v.price == null);
		if (missing >= 0) {
			const all = data.variants.every((v) => v.price == null);
			const v = data.variants[missing];
			issues.push({
				field: `variants.${missing}.price`,
				level: "error",
				message: all
					? "Нет цены: в материалах она не нашлась"
					: `Нет цены у варианта ${[v?.size, v?.color].filter(Boolean).join(" · ") || "без размера"}`,
			});
		}
		if (data.variants.some((v) => v.price != null && v.price > 0 && v.price < 100)) {
			issues.push({ field: "variants", level: "warning", message: "Подозрительно низкая цена — меньше 100 ₽" });
		}
	}
	if (!data.categoryId && !options.defaultCategoryId) {
		issues.push({
			field: "categoryId",
			level: "error",
			message: data.categoryHint
				? `Категория не определена (в материалах — «${data.categoryHint}»)`
				: "Категория не определена",
		});
	}
	if (data.photoFileIds.length === 0 && data.photoUrls.length === 0) {
		issues.push({ field: "photoFileIds", level: "warning", message: "Нет фото — модель не нашла картинок к позиции" });
	}
	if (!data.description.trim()) {
		issues.push({ field: "description", level: "warning", message: "Нет описания: в материалах только название" });
	}
	if (data.confidence < 0.6) {
		issues.push({ level: "warning", message: "Модель не уверена в разборе — сверьте с источником" });
	}
	return issues;
};

const revalidate = (item: CatalogImportItem, options: CatalogImportOptions, excluded: boolean): CatalogImportItem => {
	const issues = issuesOf(item.data, options);
	const status: CatalogImportItem["status"] = excluded
		? "excluded"
		: issues.some((i) => i.level === "error")
			? "error"
			: issues.length
				? "warning"
				: "ready";
	return { ...item, issues, status, updatedAt: nowIso() };
};

// ── Хранилище заданий ────────────────────────────────────────────────────

const PARSE_STAGES: CatalogImportStage[] = ["extract", "structure", "photos", "categories", "validate"];

type ParseSim = {
	kind: "parse";
	startedAt: number;
	durations: number[];
	totals: number[];
	/** Сколько токенов будет потрачено к концу разбора. */
	target: { calls: number; tokensIn: number; tokensOut: number };
	base: CatalogImportUsage;
	/** Этап, на котором разбор сорвётся (подсказка со словом «сбой», нечитаемые файлы). */
	failAt: number | null;
	failMessage: string;
	produce: () => CatalogImportItem[];
};

type CommitSim = {
	kind: "commit";
	startedAt: number;
	perItemMs: number;
	queue: string[];
	processed: number;
};

type Store = {
	job: Omit<CatalogImportJob, "stats">;
	files: CatalogImportFile[];
	items: CatalogImportItem[];
	sources: Map<string, FileSource>;
	sim: ParseSim | CommitSim | null;
	/** Позиции, загрузка которых упадёт на первой попытке. */
	failOnce: Set<string>;
	/** Файлы со «сбоем» в имени, уже падавшие при загрузке. */
	failedUploads: Set<string>;
};

const stores = new Map<string, Store>();
const blobCache = new Map<string, Promise<Blob>>();

const modelOf = (id: string) => MODELS.find((m) => m.id === id) ?? MODELS[0]!;
const costOf = (model: string, usage: { tokensIn: number; tokensOut: number }) => {
	const m = modelOf(model);
	return (usage.tokensIn * m.priceIn + usage.tokensOut * m.priceOut) / 1_000_000;
};

const usageOf = (model: string, calls: number, tokensIn: number, tokensOut: number): CatalogImportUsage => ({
	calls,
	tokensIn,
	tokensOut,
	costUsd: costOf(model, { tokensIn, tokensOut }),
});

const addFile = (
	st: Store,
	input: {
		filename: string;
		kind: CatalogImportFileKind;
		sizeBytes: number;
		status?: CatalogImportFileStatus;
		note?: string | null;
		path?: string;
		parentId?: string | null;
		mime?: string;
		createdAt?: string;
	},
	source: FileSource,
): CatalogImportFile => {
	const id = mintId("cif");
	const file: CatalogImportFile = {
		id,
		jobId: st.job.id,
		parentId: input.parentId ?? null,
		filename: input.filename,
		path: input.path ?? "",
		mime: input.mime ?? MIME_BY_KIND[input.kind],
		sizeBytes: input.sizeBytes,
		kind: input.kind,
		status: input.status ?? "pending",
		note: input.note ?? null,
		isImage: input.kind === "image",
		usedByItems: 0,
		contentPath: `/admin/catalog-import/jobs/${st.job.id}/files/${id}/content`,
		createdAt: input.createdAt ?? nowIso(),
	};
	st.files.push(file);
	st.sources.set(id, source);
	return file;
};

const imageSource = (shape: Shape, colorName: string, label: string): FileSource => ({
	kind: "image",
	shape,
	color: COLORS[colorName] ?? "#b8b2a6",
	label,
});

const statsOf = (st: Store): CatalogImportStats => {
	const count = (s: CatalogImportItem["status"]) => st.items.filter((i) => i.status === s).length;
	return {
		files: st.files.length,
		images: st.files.filter((f) => f.isImage).length,
		items: st.items.length,
		ready: count("ready"),
		warnings: count("warning"),
		errors: count("error"),
		excluded: count("excluded"),
		committed: count("committed"),
		failed: count("failed"),
	};
};

const jobOut = (st: Store): CatalogImportJob => structuredClone({ ...st.job, stats: statsOf(st) });

const detailOut = (st: Store): CatalogImportJobDetail => {
	const used = new Map<string, number>();
	for (const item of st.items) {
		const ids = new Set([...item.data.photoFileIds, ...item.data.sourceRefs.map((r) => r.fileId)]);
		for (const id of ids) used.set(id, (used.get(id) ?? 0) + 1);
	}
	return {
		...jobOut(st),
		files: structuredClone(st.files.map((f) => ({ ...f, usedByItems: used.get(f.id) ?? 0 }))),
		items: structuredClone(st.items),
	};
};

const touch = (st: Store) => {
	st.job.updatedAt = nowIso();
};

// ── Разбор и загрузка по часам ───────────────────────────────────────────

const stageMessage = (st: Store, stage: CatalogImportStage, done: number, total: number): string => {
	switch (stage) {
		case "extract": {
			const docs = st.files.filter((f) => !f.isImage && !f.parentId);
			const file = docs[Math.min(done, docs.length - 1)];
			return file ? `Читаем «${file.filename}»` : "Читаем файлы";
		}
		case "structure":
			return `Модель размечает фрагмент ${Math.min(done + 1, total)} из ${total}`;
		case "photos":
			return total ? `Сверяем фото с позициями: ${done} из ${total}` : "Фото в материалах нет";
		case "categories":
			return `Подбираем категорию: позиция ${Math.min(done + 1, total)} из ${total}`;
		case "validate":
			return "Проверяем цены, размеры и повторы артикулов";
		case "commit":
			return "Загружаем товары";
	}
};

const finalFileOutcome = (file: CatalogImportFile): { status: CatalogImportFileStatus; note: string | null } => {
	if (file.status === "skipped" || file.status === "failed") return { status: file.status, note: file.note };
	if (file.kind === "other") return { status: "skipped", note: "Формат не читаем — файл пропущен" };
	if (/битый|повреж/i.test(file.filename)) return { status: "failed", note: "Файл повреждён — не открылся" };
	if (/реквизит/i.test(file.filename)) return { status: "parsed", note: "Реквизиты — взяли в карточку магазина" };
	return { status: "parsed", note: file.note };
};

const tick = (st: Store, now = Date.now()) => {
	const sim = st.sim;
	if (!sim) return;
	if (sim.kind === "parse") tickParse(st, sim, now);
	else tickCommit(st, sim, now);
};

const tickParse = (st: Store, sim: ParseSim, now: number) => {
	const elapsed = Math.max(0, now - sim.startedAt);
	const totalMs = sim.durations.reduce((a, b) => a + b, 0);
	let stageIndex = sim.durations.length;
	let frac = 1;
	let acc = 0;
	for (const [i, duration] of sim.durations.entries()) {
		if (elapsed < acc + duration) {
			stageIndex = i;
			frac = (elapsed - acc) / duration;
			break;
		}
		acc += duration;
	}
	// Чтение файлов идёт без модели — токены начинают тратиться с разметки.
	const extractMs = sim.durations[0] ?? 0;
	const spent = Math.min(1, Math.max(0, (elapsed - extractMs) / Math.max(1, totalMs - extractMs)));
	st.job.usage = usageOf(
		st.job.model,
		sim.base.calls + Math.round(sim.target.calls * spent),
		sim.base.tokensIn + Math.round(sim.target.tokensIn * spent),
		sim.base.tokensOut + Math.round(sim.target.tokensOut * spent),
	);

	if (sim.failAt != null && (stageIndex > sim.failAt || (stageIndex === sim.failAt && frac >= 0.45))) {
		st.sim = null;
		st.job.status = "failed";
		st.job.error = sim.failMessage;
		for (const file of st.files) if (file.status === "pending") Object.assign(file, finalFileOutcome(file));
		st.job.progress = { ...st.job.progress, stage: PARSE_STAGES[sim.failAt] ?? "structure" };
		touch(st);
		return;
	}

	const docs = st.files.filter((f) => !f.isImage);
	if (stageIndex >= sim.durations.length) {
		for (const file of st.files) Object.assign(file, finalFileOutcome(file));
		st.items = sim.produce();
		st.sim = null;
		st.job.status = "review";
		st.job.error = null;
		st.job.progress = { stage: null, done: 0, total: 0, message: `Готово: позиций — ${st.items.length}` };
		// Новый магазин: модель дописывает в анкету то, чего не было в заявке.
		if (st.job.target.kind === "new_shop") {
			const draft = st.job.shopDraft ?? emptyShopDraft();
			const images = st.files.filter((f) => f.isImage && f.status !== "failed");
			st.job.shopDraft = {
				...draft,
				shopName: draft.shopName || st.job.application?.shopName || "Лён и Хлопок",
				brandDescription:
					draft.brandDescription ||
					"Одежда и аксессуары из льна и хлопка: платья, рубашки, брюки. Шьём малыми партиями из тканей российских фабрик.",
				mainProduct: draft.mainProduct || "Льняные платья и рубашки",
				categories: draft.categories.length ? draft.categories : ["Женщинам"],
				logoFileId: draft.logoFileId ?? null,
				coverFileId: draft.coverFileId ?? images[0]?.id ?? null,
			};
		}
		const hasTemplate = st.files.some((f) => /шаблон/i.test(f.filename));
		st.job.mode = hasTemplate ? (st.files.some((f) => !/шаблон/i.test(f.filename) && !f.isImage) ? "mixed" : "template") : "ai";
		touch(st);
		return;
	}

	const stage = PARSE_STAGES[stageIndex] ?? "extract";
	const total = sim.totals[stageIndex] ?? 0;
	const done = Math.min(total, Math.floor(frac * total));
	if (stage === "extract") {
		docs.forEach((file, i) => {
			if (i < done) Object.assign(file, finalFileOutcome(file));
		});
	} else {
		for (const file of docs) if (file.status === "pending") Object.assign(file, finalFileOutcome(file));
		if (stage !== "photos" || done >= total) {
			for (const file of st.files) if (file.status === "pending") Object.assign(file, finalFileOutcome(file));
		}
	}
	st.job.progress = { stage, done, total, message: stageMessage(st, stage, done, total) };
};

const tickCommit = (st: Store, sim: CommitSim, now: number) => {
	const due = Math.min(sim.queue.length, Math.floor((now - sim.startedAt) / sim.perItemMs));
	for (; sim.processed < due; sim.processed++) {
		const item = st.items.find((i) => i.id === sim.queue[sim.processed]);
		if (!item) continue;
		if (st.failOnce.has(item.id)) {
			st.failOnce.delete(item.id);
			item.status = "failed";
			item.issues = [
				...issuesOf(item.data, st.job.options),
				{ level: "error", message: FAIL_MESSAGES[hash(item.id) % FAIL_MESSAGES.length] ?? "Не загрузилось" },
			];
		} else {
			item.status = "committed";
			item.productId = item.existingProductId ?? `prd_imp_${hash(item.id).toString(36)}`;
			item.issues = issuesOf(item.data, st.job.options).filter((i) => i.level === "warning");
		}
		item.updatedAt = nowIso();
	}
	const next = st.items.find((i) => i.id === sim.queue[sim.processed]);
	st.job.progress = {
		stage: "commit",
		done: sim.processed,
		total: sim.queue.length,
		message: next ? `Создаём «${next.data.name}»` : "Завершаем",
	};
	if (sim.processed < sim.queue.length) return;
	st.sim = null;
	st.job.status = "done";
	st.job.committedAt = nowIso();
	if (!st.job.shop) {
		st.job.shop = {
			id: `usr_imp_${hash(st.job.id).toString(36)}`,
			name: st.job.shopDraft?.shopName || st.job.title,
		};
	}
	const committed = st.items.filter((i) => i.status === "committed").length;
	st.job.progress = {
		stage: "commit",
		done: sim.queue.length,
		total: sim.queue.length,
		message: `Загружено ${committed} из ${st.items.filter((i) => i.status !== "excluded").length}`,
	};
	touch(st);
};

const startCommit = (st: Store, ids: string[], perItemMs: number, startedAt = Date.now()) => {
	st.sim = { kind: "commit", startedAt, perItemMs, queue: ids, processed: 0 };
	st.job.status = "committing";
	st.job.error = null;
	st.job.progress = { stage: "commit", done: 0, total: ids.length, message: "Готовим загрузку" };
	touch(st);
};

// ── Генераторы содержимого для разбора ───────────────────────────────────

const EMPTY_SOCIALS: CatalogImportShopDraft["socials"] = {
	website: "",
	instagram: "",
	vk: "",
	telegram: "",
	youtube: "",
	rutube: "",
	tiktok: "",
	other: "",
};

const emptyShopDraft = (shopName = ""): CatalogImportShopDraft => ({
	shopName,
	brandDescription: "",
	mainProduct: "",
	city: "",
	categories: [],
	socials: { ...EMPTY_SOCIALS },
});

const LINEN_SHOP: CatalogImportShopDraft = {
	shopName: "Лён и Хлопок",
	brandDescription:
		"Небольшое ателье из Иваново: шьём одежду из льна и хлопка местных фабрик малыми партиями. Свободные силуэты, природные цвета и вещи, которые носятся годами.",
	mainProduct: "Льняные платья и рубашки",
	city: "Иваново",
	categories: ["Женщинам", "Мужчинам"],
	foundingDate: "2019",
	socials: {
		website: "lenihlopok.ru",
		instagram: "",
		vk: "vk.com/lenihlopok",
		telegram: "t.me/lenihlopok",
		youtube: "",
		rutube: "",
		tiktok: "",
		other: "",
	},
	logoFileId: null,
	coverFileId: null,
	legal: { inn: "370259123456", fullName: "ИП Смирнова М. О." },
	contact: { name: "Марина", phone: "+7 910 555-14-27", email: "hello@lenihlopok.ru" },
};

/**
 * Что «нашла» модель в материалах нового задания: позиции из летней коллекции,
 * фото — по кругу из загруженных картинок, источник — первая таблица/PDF.
 */
const produceFromMaterials = (st: Store): (() => CatalogImportItem[]) => {
	const images = st.files.filter((f) => f.isImage && f.status !== "failed");
	const docs = st.files.filter((f) => !f.isImage && f.kind !== "archive" && f.kind !== "other");
	const count = itemEstimate(st);
	const pool = LINEN;
	return () => {
		const photos = new Map<number, string[]>();
		const perItem = images.length >= count * 2 ? 2 : 1;
		let cursor = 0;
		for (let i = 0; i < count && cursor < images.length; i++) {
			photos.set(
				i,
				images.slice(cursor, cursor + perItem).map((f) => f.id),
			);
			cursor += perItem;
		}
		const quirks = new Map<number, Quirk[]>([
			[3, ["lowConfidence"]],
			[7, ["somePriceMissing"]],
			[11, ["noDescription"]],
		]);
		const ctx: BuildContext = {
			jobId: st.job.id,
			brand: st.job.options.defaultBrand || st.job.shopDraft?.shopName || st.job.shop?.name || "",
			prefix: "IMP",
			photos,
			sourceFileId: docs.find((f) => f.kind === "spreadsheet")?.id ?? docs[0]?.id ?? null,
			pdfFileId: docs.find((f) => f.kind === "pdf")?.id ?? null,
			quirks,
			options: st.job.options,
			seed: st.job.id,
		};
		return Array.from({ length: count }, (_, i) => buildItem(pool[i % pool.length]!, i, ctx));
	};
};

const itemEstimate = (st: Store) => {
	const images = st.files.filter((f) => f.isImage).length;
	const hasTable = st.files.some((f) => f.kind === "spreadsheet" || f.kind === "pdf" || f.kind === "document");
	if (hasTable) return 24;
	if (images) return Math.min(18, Math.max(2, Math.ceil(images / 2)));
	return 6;
};

const startParse = (st: Store, opts: { scale?: number; startedAt?: number; produce?: () => CatalogImportItem[] } = {}) => {
	const scale = opts.scale ?? 1;
	const docs = st.files.filter((f) => !f.isImage).length;
	const images = st.files.filter((f) => f.isImage).length;
	const estimate = itemEstimate(st);
	const vision = modelOf(st.job.model).vision && st.job.options.matchPhotosByVision;
	// Пропущенные и нечитаемые (PDF с паролем) повторный разбор не перечитывает.
	for (const file of st.files) {
		if (file.status !== "skipped" && file.status !== "failed") file.status = "pending";
	}
	const readable = st.files.filter((f) => f.status === "pending");
	st.items = [];
	st.sim = {
		kind: "parse",
		startedAt: opts.startedAt ?? Date.now(),
		durations: [2500, 6000, images ? 3500 : 800, 2000, 1500].map((ms) => ms * scale),
		totals: [Math.max(1, docs), Math.max(1, Math.ceil(estimate / 8)), images, estimate, estimate],
		target: {
			calls: Math.ceil(estimate / 8) + (vision ? Math.ceil(images / 4) : 0) + Math.ceil(estimate / 10) + 1,
			tokensIn: 9000 * Math.max(1, docs) + (vision ? 2600 * images : 0) + 1400 * estimate,
			tokensOut: 650 * estimate + (vision ? 120 * images : 0),
		},
		base: { ...st.job.usage },
		failAt: readable.length === 0 ? 0 : /сбой/i.test(st.job.instructions) ? 1 : null,
		failMessage:
			readable.length === 0
				? "Ни один файл задания не прочитался. Добавьте прайс, фото или текст и разберите заново."
				: "OpenRouter ответил 429: превышен лимит запросов к модели. Подождите минуту и разберите заново — прочитанные файлы повторно не читаются.",
		produce: opts.produce ?? produceFromMaterials(st),
	};
	st.job.status = "parsing";
	st.job.error = null;
	st.job.progress = { stage: "extract", done: 0, total: Math.max(1, docs), message: "Читаем файлы" };
	touch(st);
};

// ── Начальный набор ──────────────────────────────────────────────────────

const newStore = (job: Partial<Omit<CatalogImportJob, "stats">> & Pick<CatalogImportJob, "id" | "title" | "status" | "target">): Store => {
	const st: Store = {
		job: {
			options: { ...DEFAULT_OPTIONS },
			instructions: "",
			model: DEFAULT_MODEL,
			mode: null,
			shop: null,
			application: null,
			shopDraft: null,
			progress: { ...IDLE },
			usage: { ...ZERO_USAGE },
			error: null,
			createdBy: { id: "usr_admin", name: "Платформа" },
			createdAt: ago(60),
			updatedAt: ago(30),
			committedAt: null,
			...job,
		},
		files: [],
		items: [],
		sources: new Map(),
		sim: null,
		failOnce: new Set(),
		failedUploads: new Set(),
	};
	stores.set(st.job.id, st);
	return st;
};

const FOLDER: Partial<Record<string, string>> = {
	"cat-1-1": "Платья",
	"cat-1-2": "Юбки",
	"cat-1-3": "Рубашки и блузы",
	"cat-w-pants": "Брюки и шорты",
	"cat-2-1": "Мужское",
	"cat-2-2": "Мужское",
	"cat-1-4": "Жакеты",
	"cat-w-knit": "Трикотаж",
	"cat-w-tshirts": "Трикотаж",
	"cat-w-bags": "Аксессуары",
	"cat-w-hats": "Аксессуары",
	"cat-w-acc-other": "Аксессуары",
};

const PRICE_CSV = (rows: Proto[], wholesale = true) =>
	[
		"Артикул;Наименование;Цвет;Размеры;Цена опт;Состав",
		...rows.map((p) =>
			[p.code, p.name, p.colors.join("/"), p.sizes.join(",") || "—", wholesale ? Math.round(p.price / 2) : p.price, p.comp ?? ""].join(";"),
		),
	].join("\n");

const seedLinen = () => {
	const st = newStore({
		id: "imp_seed_linen",
		title: "Лён и Хлопок — летняя коллекция",
		status: "review",
		target: { kind: "new_shop", applicationId: "app_seed_linen", sendInvite: true },
		application: { id: "app_seed_linen", shopName: "Лён и Хлопок" },
		instructions: "Цены в прайсе оптовые, розница ×2. Это летняя коллекция, размеры 42–50.",
		createdBy: { id: "usr_mgr_irina", name: "Ирина Соколова" },
		createdAt: ago(60 * 26),
		updatedAt: ago(45),
		mode: "ai",
		usage: usageOf(DEFAULT_MODEL, 31, 412_380, 96_140),
		progress: { stage: null, done: 0, total: 0, message: "Готово: позиций — 40" },
		shopDraft: structuredClone(LINEN_SHOP),
	});
	const at = ago(60 * 26);
	const price = addFile(
		st,
		{ filename: "Прайс Лён и Хлопок лето 2026.csv", kind: "spreadsheet", sizeBytes: 18_420, status: "parsed", createdAt: at },
		{ kind: "text", text: PRICE_CSV(LINEN), mime: "text/csv" },
	);
	const pdf = addFile(
		st,
		{ filename: "Лукбук лето 2026.pdf", kind: "pdf", sizeBytes: 8_712_004, status: "parsed", createdAt: at },
		{ kind: "pdf", lines: ["Len i Hlopok - lookbook leto 2026", "Platya, rubashki, bryuki iz lna", "Razmery 42-50, opt ot 10 sht."] },
	);
	const archive = addFile(
		st,
		{ filename: "Фото коллекции.zip", kind: "archive", sizeBytes: 46_204_118, status: "parsed", note: "Распаковано файлов: 66", createdAt: at },
		{ kind: "text", text: "", mime: "application/zip" },
	);
	const logo = addFile(
		st,
		{ filename: "logo.png", path: "Логотип/logo.png", parentId: archive.id, kind: "image", sizeBytes: 48_311, status: "parsed", createdAt: at },
		imageSource("logo", "оливковый", "Логотип/logo.png"),
	);
	const cover = addFile(
		st,
		{ filename: "cover.jpg", path: "Обложка/cover.jpg", parentId: archive.id, kind: "image", sizeBytes: 912_440, status: "parsed", createdAt: at },
		imageSource("cover", "бежевый", "Обложка/cover.jpg"),
	);
	st.job.shopDraft = { ...LINEN_SHOP, logoFileId: logo.id, coverFileId: cover.id };

	const quirks = new Map<number, Quirk[]>([
		[5, ["noPhoto"]],
		[17, ["noPhoto"]],
		[29, ["noPhoto"]],
		[36, ["noPhoto"]],
		[8, ["lowConfidence"]],
		[24, ["lowConfidence"]],
		[25, ["lowConfidence"]],
		[12, ["somePriceMissing"]],
		[22, ["noPrice"]],
		[14, ["noDescription"]],
		[38, ["excluded"]],
		[33, ["photoUrl"]],
	]);
	const photos = new Map<number, string[]>();
	LINEN.forEach((proto, i) => {
		const q = quirks.get(i) ?? [];
		if (q.includes("noPhoto") || q.includes("photoUrl")) return;
		const folder = `${FOLDER[proto.cat ?? ""] ?? "Разное"}/${proto.name.replace(/[«»"]/g, "")}`;
		const shots = i % 3 === 0 ? 1 : 2;
		photos.set(
			i,
			Array.from({ length: shots }, (_, n) => {
				const color = proto.colors[n % proto.colors.length] ?? "бежевый";
				const path = `${folder}/${n + 1}.jpg`;
				return addFile(
					st,
					{ filename: `${n + 1}.jpg`, path, parentId: archive.id, kind: "image", sizeBytes: 420_000 + ((i * 7919 + n * 104_729) % 900_000), status: "parsed", createdAt: at },
					imageSource(proto.shape, color, path),
				).id;
			}),
		);
	});
	addFile(
		st,
		{ filename: "Переписка WhatsApp с Мариной.txt", kind: "text", sizeBytes: 6_140, status: "parsed", createdAt: at },
		{
			kind: "text",
			mime: "text/plain",
			text: [
				"[12.09.2026, 10:14] Марина: Добрый день! Скинула прайс и лукбук, фото в архиве по папкам.",
				"[12.09.2026, 10:15] Марина: Цены в прайсе оптовые, в розницу у нас ×2.",
				"[12.09.2026, 10:17] Марина: Размеры 42–50, мужское 46–54. Шопер и косметичка — без размера.",
				"[12.09.2026, 10:20] Ирина: Принято, заведём черновиком, покажу перед публикацией.",
				"[12.09.2026, 10:21] Марина: Пояс-кушак пока не ставим, ждём ткань.",
			].join("\n"),
		},
	);
	addFile(
		st,
		{ filename: "Реквизиты ИП.docx", kind: "document", sizeBytes: 21_904, status: "parsed", note: "Реквизиты — взяли в карточку магазина", createdAt: at },
		{ kind: "text", text: "ИП Смирнова М. О.\nИНН 370259123456", mime: "text/plain" },
	);
	addFile(
		st,
		{ filename: "IMG_4521.HEIC", kind: "image", sizeBytes: 3_204_551, status: "failed", note: "Файл повреждён — не открылся", createdAt: at },
		imageSource("dress", "серый", "IMG_4521.HEIC"),
	);
	st.items = LINEN.map((proto, i) =>
		buildItem(proto, i, {
			jobId: st.job.id,
			brand: "Лён и Хлопок",
			prefix: "LH",
			photos,
			sourceFileId: price.id,
			pdfFileId: pdf.id,
			quirks,
			options: st.job.options,
			seed: st.job.id,
		}),
	);
};

const seedSilverMoon = () => {
	const st = newStore({
		id: "imp_seed_silver",
		title: "Silver Moon — осенние украшения",
		status: "draft",
		target: { kind: "existing_shop", shopId: "shop-silver-moon" },
		shop: { id: "shop-silver-moon", name: "Silver Moon" },
		instructions: "Размеры колец — российские. Цепочки в прайсе в сантиметрах.",
		model: "anthropic/claude-sonnet-4.5",
		createdAt: ago(12),
		updatedAt: ago(1),
	});
	const at = ago(10);
	const price = addFile(
		st,
		{ filename: "Прайс Silver Moon осень.csv", kind: "spreadsheet", sizeBytes: 7_812, createdAt: at },
		{ kind: "text", text: PRICE_CSV(JEWELRY, false), mime: "text/csv" },
	);
	const archive = addFile(
		st,
		{ filename: "Фото украшений.zip", kind: "archive", sizeBytes: 21_440_903, note: "Распаковано файлов: 14", createdAt: at },
		{ kind: "text", text: "", mime: "application/zip" },
	);
	const photos = new Map<number, string[]>();
	JEWELRY.forEach((proto, i) => {
		const path = `${proto.code}/${proto.code}-1.jpg`;
		photos.set(i, [
			addFile(
				st,
				{ filename: `${proto.code}-1.jpg`, path, parentId: archive.id, kind: "image", sizeBytes: 640_000 + i * 12_345, createdAt: at },
				imageSource("ring", proto.colors[0] ?? "серебро", path),
			).id,
		]);
	});
	addFile(
		st,
		{ filename: "Описание коллекции.txt", kind: "text", sizeBytes: 2_310, createdAt: at },
		{ kind: "text", mime: "text/plain", text: "Осенняя коллекция Silver Moon: серебро 925, родий, натуральные камни." },
	);
	// Разбор идёт медленно (около двух с половиной минут): этапы успевает
	// увидеть и человек, и скриншот.
	startParse(st, {
		scale: 9,
		startedAt: BOOT - 4_000,
		produce: () =>
			JEWELRY.map((proto, i) =>
				buildItem(proto, i, {
					jobId: st.job.id,
					brand: "Silver Moon",
					prefix: "SM",
					photos,
					sourceFileId: price.id,
					quirks: new Map([
						[6, ["lowConfidence"]],
						[11, ["somePriceMissing"]],
					]),
					options: st.job.options,
					seed: st.job.id,
				}),
			),
	});
};

const seedAnna = () => {
	const st = newStore({
		id: "imp_seed_anna",
		title: "Мастерская Анны — прайс на зиму",
		status: "draft",
		target: { kind: "existing_shop", shopId: "shop-handmade" },
		shop: { id: "shop-handmade", name: "Мастерская Анны" },
		createdBy: { id: "usr_mgr_irina", name: "Ирина Соколова" },
		createdAt: ago(95),
		updatedAt: ago(90),
	});
	const at = ago(92);
	addFile(
		st,
		{ filename: "Прайс зима 2026.csv", kind: "spreadsheet", sizeBytes: 5_204, createdAt: at },
		{ kind: "text", mime: "text/csv", text: PRICE_CSV(LINEN.slice(26, 33), false) },
	);
	addFile(st, { filename: "IMG_0141.jpg", kind: "image", sizeBytes: 2_812_331, createdAt: at }, imageSource("shirt", "молочный", "IMG_0141.jpg"));
	addFile(st, { filename: "IMG_0142.jpg", kind: "image", sizeBytes: 2_640_118, createdAt: at }, imageSource("shirt", "синий", "IMG_0142.jpg"));
};

const seedNatur = () => {
	const st = newStore({
		id: "imp_seed_natur",
		title: "НатурТекстиль — домашний текстиль",
		status: "done",
		target: { kind: "existing_shop", shopId: "shop-natur" },
		shop: { id: "shop-natur", name: "НатурТекстиль" },
		createdAt: ago(60 * 24 * 3),
		updatedAt: ago(60 * 24 * 3 - 40),
		committedAt: ago(60 * 24 * 3 - 40),
		mode: "mixed",
		usage: usageOf(DEFAULT_MODEL, 14, 188_904, 41_220),
		options: { ...DEFAULT_OPTIONS, publishMode: "moderation" },
		progress: { stage: "commit", done: 22, total: 22, message: "Загружено 19 из 22" },
	});
	const at = ago(60 * 24 * 3);
	const tpl = addFile(
		st,
		{ filename: "Шаблон загрузки каталога OSMIX — НатурТекстиль.csv", kind: "spreadsheet", sizeBytes: 11_002, status: "parsed", createdAt: at },
		{ kind: "text", mime: "text/csv", text: PRICE_CSV(TEXTILE, false) },
	);
	const photos = new Map<number, string[]>();
	TEXTILE.forEach((proto, i) => {
		if (i % 5 === 4) return;
		photos.set(i, [
			addFile(
				st,
				{ filename: `natur-${i + 1}.jpg`, kind: "image", sizeBytes: 380_000 + i * 9_001, status: "parsed", createdAt: at },
				imageSource("textile", proto.colors[0] ?? "натуральный", `natur-${i + 1}.jpg`),
			).id,
		]);
	});
	st.items = TEXTILE.map((proto, i) =>
		buildItem(proto, i, {
			jobId: st.job.id,
			brand: "НатурТекстиль",
			prefix: "NT",
			photos,
			sourceFileId: tpl.id,
			quirks: new Map([
				[9, ["excluded"]],
				[20, ["excluded"]],
			]),
			options: st.job.options,
			seed: st.job.id,
		}),
	);
	const failed = new Map([
		[3, FAIL_MESSAGES[0]],
		[12, FAIL_MESSAGES[2]],
		[17, FAIL_MESSAGES[1]],
	]);
	st.items.forEach((item, i) => {
		if (item.status === "excluded") return;
		const message = failed.get(i);
		if (message) {
			item.status = "failed";
			item.issues = [...item.issues, { level: "error", message }];
			return;
		}
		item.status = "committed";
		// Пятая часть — уже были в магазине с тем же артикулом: загрузка обновила их.
		if (i % 5 === 1) item.existingProductId = `prd_natur_${i + 1}`;
		item.productId = item.existingProductId ?? `prd_imp_natur_${i + 1}`;
		item.issues = item.issues.filter((x) => x.level === "warning");
	});
};

const seedLina = () => {
	const st = newStore({
		id: "imp_seed_lina",
		title: "Мастерская Лины — керамика",
		status: "review",
		target: { kind: "existing_shop", shopId: "shop-lina" },
		shop: { id: "shop-lina", name: "Мастерская Лины" },
		createdBy: { id: "usr_mgr_irina", name: "Ирина Соколова" },
		createdAt: ago(40),
		updatedAt: ago(2),
		mode: "ai",
		usage: usageOf("openai/gpt-4.1-mini", 9, 96_310, 18_904),
		model: "openai/gpt-4.1-mini",
	});
	const at = ago(40);
	const text = addFile(
		st,
		{ filename: "Список изделий от Лины.txt", kind: "text", sizeBytes: 3_904, status: "parsed", createdAt: at },
		{ kind: "text", mime: "text/plain", text: CERAMICS.map((p) => `${p.name} — ${p.price} руб.`).join("\n") },
	);
	const photos = new Map<number, string[]>();
	CERAMICS.forEach((proto, i) => {
		photos.set(i, [
			addFile(
				st,
				{ filename: `lina-${i + 1}.jpg`, kind: "image", sizeBytes: 510_000 + i * 7_777, status: "parsed", createdAt: at },
				imageSource("ceramic", proto.colors[0] ?? "бежевый", `lina-${i + 1}.jpg`),
			).id,
		]);
	});
	st.items = CERAMICS.map((proto, i) =>
		buildItem(proto, i, {
			jobId: st.job.id,
			brand: "Мастерская Лины",
			prefix: "ML",
			photos,
			sourceFileId: text.id,
			options: st.job.options,
			seed: st.job.id,
		}),
	);
	// Загрузка в разгаре: по позиции в 2,5 с, две упадут — их можно повторить.
	const ids = st.items.map((i) => i.id);
	st.failOnce = new Set([ids[4], ids[13]].filter((x): x is string => !!x));
	startCommit(st, ids, 2_500, BOOT - 9_000);
};

const seedUyut = () => {
	const st = newStore({
		id: "imp_seed_uyut",
		title: "Уютный дом — каталог PDF",
		status: "failed",
		target: { kind: "existing_shop", shopId: "shop-uyut" },
		shop: { id: "shop-uyut", name: "Уютный дом" },
		createdAt: ago(60 * 5),
		updatedAt: ago(60 * 5 - 3),
		error:
			"Не удалось прочитать «Каталог Уютный дом 2026.pdf»: файл защищён паролем, а других материалов в задании нет. Добавьте прайс или PDF без пароля и разберите заново.",
		progress: { stage: "extract", done: 0, total: 1, message: "Читаем «Каталог Уютный дом 2026.pdf»" },
	});
	addFile(
		st,
		{ filename: "Каталог Уютный дом 2026.pdf", kind: "pdf", sizeBytes: 14_402_118, status: "failed", note: "PDF защищён паролем", createdAt: ago(60 * 5) },
		{ kind: "pdf", lines: ["Uyutny dom - katalog 2026"] },
	);
};

const seedArtcraft = () => {
	newStore({
		id: "imp_seed_artcraft",
		title: "ArtCraft — пробная загрузка",
		status: "cancelled",
		target: { kind: "existing_shop", shopId: "shop-artcraft" },
		shop: { id: "shop-artcraft", name: "ArtCraft" },
		createdAt: ago(60 * 24 * 6),
		updatedAt: ago(60 * 24 * 6 - 5),
	});
};

let seededOnce = false;
const ensureSeeded = () => {
	if (seededOnce) return;
	seededOnce = true;
	seedArtcraft();
	seedNatur();
	seedUyut();
	seedLinen();
	seedAnna();
	seedLina();
	seedSilverMoon();
};

// ── Помощники методов ────────────────────────────────────────────────────

const load = (id: string): Store => {
	ensureSeeded();
	const st = stores.get(id);
	if (!st) throw new ApiError("Задание загрузки не найдено", 404, "NOT_FOUND");
	tick(st);
	return st;
};

const BUSY = new Set<CatalogImportJob["status"]>(["parsing", "committing"]);
const assertMaterialsEditable = (st: Store) => {
	if (BUSY.has(st.job.status)) throw new ApiError("Идёт разбор или загрузка — дождитесь окончания", 409, "JOB_BUSY");
	if (st.job.status === "done" || st.job.status === "cancelled")
		throw new ApiError("Задание закрыто — материалы больше не меняются", 409, "JOB_CLOSED");
};

const findItem = (st: Store, itemId: string) => {
	const item = st.items.find((i) => i.id === itemId);
	if (!item) throw new ApiError("Позиция не найдена", 404, "NOT_FOUND");
	if (item.status === "committed") throw new ApiError("Позиция уже загружена в магазин", 409, "ITEM_COMMITTED");
	return item;
};

/** Текущий пользователь консоли — из сохранённой сессии (без импорта стора: цикл). */
const currentUser = (): { id: string; name: string } => {
	try {
		const raw = localStorage.getItem("osmix-admin-auth");
		const user = raw ? (JSON.parse(raw) as { state?: { user?: { id?: string; name?: string } } }).state?.user : null;
		if (user?.id) return { id: user.id, name: user.name || "Платформа" };
	} catch {
		/* приватный режим — подпишем общим именем */
	}
	return { id: "usr_admin", name: "Платформа" };
};

/**
 * Имена магазина и заявки — из активной реализации api (на стенде это шлюз,
 * офлайн — моки). Импорт ленивый: клиент сам импортирует моки.
 */
const resolveTarget = async (
	target: CatalogImportTarget,
): Promise<{ shop: CatalogImportJob["shop"]; application: CatalogImportJob["application"] }> => {
	const { api } = await import("../client");
	if (target.kind === "existing_shop") {
		const [sellers, storefronts] = await Promise.all([
			api.sellers.list().catch(() => []),
			api.storefronts.list().catch(() => []),
		]);
		const name =
			storefronts.find((s) => s.id === target.shopId)?.name ??
			sellers.find((s) => s.shopId === target.shopId || s.id === target.shopId)?.shopName;
		if (!name) throw new ApiError("Магазин не найден", 404, "SHOP_NOT_FOUND");
		return { shop: { id: target.shopId, name }, application: null };
	}
	if (!target.applicationId) return { shop: null, application: null };
	const board = await api.applications.board().catch(() => null);
	const app = board?.applications.find((a) => a.id === target.applicationId);
	return {
		shop: null,
		application: { id: target.applicationId, shopName: app?.shopName ?? "Заявка без названия" },
	};
};

const ddmmyyyy = (d = new Date()) => d.toLocaleDateString("ru-RU");

// ── Реализация домена ────────────────────────────────────────────────────

export const catalogImport: Api["catalogImport"] = {
	jobs: async (query = {}) => {
		ensureSeeded();
		await sleep(220);
		return [...stores.values()]
			.filter((st) => {
				tick(st);
				if (query.applicationId && st.job.application?.id !== query.applicationId) return false;
				if (query.shopId && st.job.shop?.id !== query.shopId) return false;
				return true;
			})
			.sort((a, b) => b.job.createdAt.localeCompare(a.job.createdAt))
			.map(jobOut);
	},

	create: async (input) => {
		ensureSeeded();
		await sleep(380);
		const target: CatalogImportTarget =
			input.target.kind === "new_shop" ? { ...input.target, sendInvite: input.target.sendInvite ?? false } : input.target;
		if (target.kind === "new_shop" && !target.applicationId) {
			const c = target.contact;
			if (!c?.name?.trim() && !c?.phone?.trim() && !c?.email?.trim()) {
				throw new ApiError("Укажите заявку или хотя бы один контакт продавца", 400, "VALIDATION");
			}
		}
		const names = await resolveTarget(target);
		const contactName = target.kind === "new_shop" ? target.contact?.name?.trim() : "";
		const who = names.shop?.name ?? names.application?.shopName ?? (contactName || "новый магазин");
		const model = input.model?.trim() || DEFAULT_MODEL;
		const st = newStore({
			id: mintId("imp"),
			title: input.title?.trim() || `Каталог «${who}» от ${ddmmyyyy()}`,
			status: "draft",
			target,
			options: { ...DEFAULT_OPTIONS, ...input.options },
			instructions: input.instructions ?? "",
			model,
			shop: names.shop,
			application: names.application,
			shopDraft:
				target.kind === "new_shop"
					? { ...emptyShopDraft(names.application?.shopName ?? ""), contact: target.contact }
					: null,
			createdBy: currentUser(),
			createdAt: nowIso(),
			updatedAt: nowIso(),
		});
		return jobOut(st);
	},

	job: async (id) => {
		await sleep(180);
		return detailOut(load(id));
	},

	update: async (id, patch) => {
		await sleep(260);
		const st = load(id);
		if (patch.target) {
			const names = await resolveTarget(patch.target);
			st.job.target = patch.target;
			st.job.shop = names.shop;
			st.job.application = names.application;
		}
		if (patch.title !== undefined) st.job.title = patch.title.trim() || st.job.title;
		if (patch.instructions !== undefined) st.job.instructions = patch.instructions;
		if (patch.model !== undefined) st.job.model = patch.model || DEFAULT_MODEL;
		if (patch.options) st.job.options = { ...st.job.options, ...patch.options };
		if (patch.shopDraft) {
			const base = st.job.shopDraft ?? emptyShopDraft();
			st.job.shopDraft = {
				...base,
				...patch.shopDraft,
				socials: { ...base.socials, ...patch.shopDraft.socials },
			};
		}
		// Категория по умолчанию снимает «категория не определена» с позиций.
		if (patch.options && st.job.status === "review") {
			st.items = st.items.map((item) =>
				item.status === "committed" ? item : revalidate(item, st.job.options, item.status === "excluded"),
			);
		}
		touch(st);
		return jobOut(st);
	},

	cancel: async (id) => {
		await sleep(300);
		const st = load(id);
		if (st.job.status === "committing" || st.job.status === "done") {
			throw new ApiError("Товары уже загружаются или загружены — отменить задание нельзя", 409, "JOB_COMMITTED");
		}
		// Как на шлюзе: отмена удаляет материалы и черновики.
		st.sim = null;
		st.files = [];
		st.items = [];
		st.sources.clear();
		st.job.status = "cancelled";
		st.job.progress = { ...IDLE };
		touch(st);
	},

	uploadFile: async (id, file) => {
		const st = load(id);
		assertMaterialsEditable(st);
		if (file.size > CATALOG_IMPORT_FILE_MAX_BYTES) {
			throw new ApiError(`«${file.name}» больше 100 МБ — разбейте архив на части`, 413, "FILE_TOO_LARGE");
		}
		// Скорость «сети» ~8 МБ/с: крупный архив грузится заметно дольше фото.
		await sleep(Math.min(4_000, 300 + (file.size / (8 * 1024 * 1024)) * 1000));
		if (/сбой|fail/i.test(file.name) && !st.failedUploads.has(file.name)) {
			st.failedUploads.add(file.name);
			throw new ApiError("Связь прервалась на загрузке файла — повторите", 502, "UPLOAD_FAILED");
		}
		const kind = kindOf(file.name, file.type);
		const created: CatalogImportFile[] = [];
		const parent = addFile(
			st,
			{
				filename: file.name,
				kind,
				sizeBytes: file.size,
				mime: file.type || MIME_BY_KIND[kind],
				status: kind === "other" ? "skipped" : "pending",
				note:
					kind === "other"
						? "Формат не читаем — файл пропущен"
						: extOf(file.name) === "heic"
							? "HEIC переведём в JPEG при загрузке товара"
							: kind === "archive"
								? "Распаковано файлов: 6"
								: null,
			},
			{ kind: "upload", blob: file },
		);
		created.push(parent);
		if (kind === "archive") {
			const base = file.name.replace(/\.[^.]+$/, "");
			const shapes: [Shape, string, string][] = [
				["dress", "оливковый", "Платья/Ривьера"],
				["dress", "молочный", "Платья/Ривьера"],
				["shirt", "голубой", "Рубашки/Бриз"],
				["shirt", "белый", "Рубашки/Бриз"],
				["pants", "чёрный", "Брюки/Ветер"],
				["bag", "натуральный", "Аксессуары/Шопер"],
			];
			shapes.forEach(([shape, color, folder], i) => {
				const path = `${base}/${folder}/${(i % 2) + 1}.jpg`;
				created.push(
					addFile(
						st,
						{ filename: `${(i % 2) + 1}.jpg`, path, parentId: parent.id, kind: "image", sizeBytes: 380_000 + i * 51_000 },
						imageSource(shape, color, path),
					),
				);
			});
		}
		touch(st);
		return structuredClone(created);
	},

	addText: async (id, input) => {
		await sleep(250);
		const st = load(id);
		assertMaterialsEditable(st);
		const name = input.filename?.trim() || `Текст от ${ddmmyyyy()}`;
		const file = addFile(
			st,
			{ filename: /\.[a-z]{2,4}$/i.test(name) ? name : `${name}.txt`, kind: "text", sizeBytes: byteLength(input.text) },
			{ kind: "text", text: input.text, mime: "text/plain" },
		);
		touch(st);
		return structuredClone(file);
	},

	addFromApplication: async (id, _input) => {
		await sleep(700);
		const st = load(id);
		assertMaterialsEditable(st);
		if (!st.job.application) throw new ApiError("Задание не привязано к заявке", 400, "NO_APPLICATION");
		const created: CatalogImportFile[] = [];
		created.push(
			addFile(
				st,
				{ filename: "Прайс от продавца.csv", kind: "spreadsheet", sizeBytes: 9_311 },
				{ kind: "text", mime: "text/csv", text: PRICE_CSV(LINEN.slice(0, 12)) },
			),
		);
		const archive = addFile(
			st,
			{ filename: "Фото из заявки.zip", kind: "archive", sizeBytes: 12_904_331, note: "Распаковано файлов: 4" },
			{ kind: "text", text: "", mime: "application/zip" },
		);
		created.push(archive);
		LINEN.slice(0, 4).forEach((proto, i) => {
			const path = `Фото из заявки/${proto.name.replace(/[«»"]/g, "")}.jpg`;
			created.push(
				addFile(
					st,
					{ filename: path.split("/").pop() ?? "foto.jpg", path, parentId: archive.id, kind: "image", sizeBytes: 640_000 + i * 31_000 },
					imageSource(proto.shape, proto.colors[0] ?? "бежевый", path),
				),
			);
		});
		touch(st);
		return structuredClone(created);
	},

	deleteFile: async (id, fileId) => {
		await sleep(200);
		const st = load(id);
		assertMaterialsEditable(st);
		const gone = new Set([fileId, ...st.files.filter((f) => f.parentId === fileId).map((f) => f.id)]);
		if (!st.files.some((f) => f.id === fileId)) throw new ApiError("Файл не найден", 404, "NOT_FOUND");
		st.files = st.files.filter((f) => !gone.has(f.id));
		for (const fid of gone) st.sources.delete(fid);
		// Фото удалённого файла уходят и из черновиков позиций.
		st.items = st.items.map((item) =>
			item.data.photoFileIds.some((p) => gone.has(p))
				? revalidate(
						{ ...item, data: { ...item.data, photoFileIds: item.data.photoFileIds.filter((p) => !gone.has(p)) } },
						st.job.options,
						item.status === "excluded",
					)
				: item,
		);
		touch(st);
	},

	fileBlob: async (id, fileId) => {
		const st = load(id);
		const source = st.sources.get(fileId);
		const file = st.files.find((f) => f.id === fileId);
		if (!source || !file) throw new ApiError("Файл не найден", 404, "NOT_FOUND");
		await sleep(120 + (hash(fileId) % 260));
		if (source.kind === "upload") return source.blob;
		if (source.kind === "text") return new Blob([source.text], { type: `${source.mime};charset=utf-8` });
		if (source.kind === "pdf") return makePdf(source.lines);
		let cached = blobCache.get(fileId);
		if (!cached) {
			cached = drawImage(source.shape, source.color, source.label);
			blobCache.set(fileId, cached);
		}
		return cached;
	},

	parse: async (id) => {
		await sleep(320);
		const st = load(id);
		if (BUSY.has(st.job.status)) throw new ApiError("Разбор или загрузка уже идут", 409, "JOB_BUSY");
		if (st.job.status === "done" || st.job.status === "cancelled")
			throw new ApiError("Задание закрыто — заведите новое", 409, "JOB_CLOSED");
		if (st.files.length === 0) throw new ApiError("Добавьте материалы: файл, архив или текст", 400, "NO_FILES");
		startParse(st);
		return jobOut(st);
	},

	updateItem: async (id, itemId, patch) => {
		await sleep(220);
		const st = load(id);
		const item = findItem(st, itemId);
		const data = patch.data
			? {
					...item.data,
					...patch.data,
					// Правка человеком — уже не догадка модели.
					confidence: 1,
					categoryPath:
						patch.data.categoryId !== undefined
							? patch.data.categoryId
								? (patch.data.categoryPath ?? CATEGORY_PATHS.get(patch.data.categoryId))
								: undefined
							: (patch.data.categoryPath ?? item.data.categoryPath),
				}
			: item.data;
		const excluded = patch.excluded ?? item.status === "excluded";
		const next = revalidate({ ...item, data }, st.job.options, excluded);
		st.items = st.items.map((i) => (i.id === itemId ? next : i));
		touch(st);
		return structuredClone(next);
	},

	bulkUpdate: async (id, input) => {
		await sleep(420);
		const st = load(id);
		const ids = new Set(input.itemIds);
		const { set } = input;
		let updated = 0;
		st.items = st.items.map((item) => {
			if (!ids.has(item.id) || item.status === "committed") return item;
			updated++;
			const data = { ...item.data };
			if (set.categoryId !== undefined) {
				data.categoryId = set.categoryId;
				data.categoryPath = set.categoryId ? CATEGORY_PATHS.get(set.categoryId) : undefined;
			}
			if (set.brand !== undefined) data.brand = set.brand;
			if (set.priceMarkupPercent !== undefined) {
				const k = 1 + set.priceMarkupPercent / 100;
				data.variants = data.variants.map((v) => ({
					...v,
					price: v.price == null ? null : Math.round(v.price * k),
					oldPrice: v.oldPrice == null ? v.oldPrice : Math.round(v.oldPrice * k),
				}));
			}
			const excluded = set.excluded ?? item.status === "excluded";
			return revalidate({ ...item, data }, st.job.options, excluded);
		});
		touch(st);
		return { updated };
	},

	regenerateItem: async (id, itemId, input) => {
		await sleep(1_400);
		const st = load(id);
		const item = findItem(st, itemId);
		const wish = input.instruction?.trim().toLowerCase() ?? "";
		const data = { ...item.data };
		if (input.fields.includes("description")) {
			const base = `${data.name}${data.composition ? ` из ткани «${data.composition}»` : ""}.`;
			const body = data.description || "Вещь на каждый день: свободная посадка, дышащая ткань, аккуратная обработка швов.";
			const sentences = body.split(/(?<=\.)\s+/);
			data.description = wish.includes("короч")
				? (sentences[0] ?? body)
				: `${base} ${body} ${data.color ? `Цвет — ${data.color}.` : ""} Подходит для города и отпуска.`.replace(/\s+/g, " ").trim();
			for (const word of wish.match(/без слова? «?([а-яё-]+)»?/i)?.slice(1) ?? []) {
				data.description = data.description.replace(new RegExp(word, "gi"), "").replace(/\s+/g, " ").trim();
			}
		}
		if (input.fields.includes("keywords")) {
			data.keywords = [...new Set([data.name.split(" ")[0]?.toLowerCase() ?? "", ...data.color.split(", "), "лето", "натуральная ткань"])].filter(Boolean);
		}
		if (input.fields.includes("characteristics")) {
			data.characteristics = { ...data.characteristics, Сезон: "Лето", Уход: "Деликатная стирка при 30°" };
		}
		if (input.fields.includes("category") && !data.categoryId) {
			data.categoryId = "cat-w-acc-other";
			data.categoryPath = CATEGORY_PATHS.get("cat-w-acc-other");
		}
		st.job.usage = usageOf(
			st.job.model,
			st.job.usage.calls + 1,
			st.job.usage.tokensIn + 2_300,
			st.job.usage.tokensOut + 420,
		);
		const next = revalidate({ ...item, data }, st.job.options, item.status === "excluded");
		st.items = st.items.map((i) => (i.id === itemId ? next : i));
		touch(st);
		return structuredClone(next);
	},

	commit: async (id, input = {}) => {
		await sleep(400);
		const st = load(id);
		if (st.job.status !== "review" && st.job.status !== "done") {
			throw new ApiError("Загрузить можно после разбора, на шаге проверки", 409, "JOB_NOT_READY");
		}
		if (st.job.target.kind === "new_shop" && !st.job.shopDraft?.shopName.trim()) {
			throw new ApiError("Укажите название магазина в карточке магазина", 400, "SHOP_NAME_REQUIRED");
		}
		const loadable = new Set<CatalogImportItem["status"]>(["ready", "warning", "failed"]);
		const wanted = input.itemIds?.length ? new Set(input.itemIds) : null;
		const queue = st.items
			.filter((i) => loadable.has(i.status) && (wanted ? wanted.has(i.id) : i.status !== "failed"))
			.map((i) => i.id)
			.slice(0, CATALOG_IMPORT_MAX_ITEMS);
		if (queue.length === 0) throw new ApiError("Нет позиций для загрузки: все с ошибками или исключены", 400, "NOTHING_TO_COMMIT");
		// Первая загрузка задания роняет примерно каждую десятую позицию — так
		// виден итог «часть с ошибкой» и повтор. Повтор проходит.
		if (st.job.status === "review") {
			for (const itemId of queue) if (hash(itemId) % 10 === 3) st.failOnce.add(itemId);
		}
		startCommit(st, queue, 280);
		return jobOut(st);
	},

	template: async () => {
		await sleep(300);
		// Настоящий xlsx собирает шлюз (@osmix/lib/xlsx); мок отдаёт те же
		// колонки в CSV — Excel откроет, а админка мок от шлюза не отличит.
		const header = [
			"Артикул",
			"Название",
			"Бренд",
			"Категория",
			"Цвет",
			"Размер",
			"Размер РФ",
			"Цена",
			"Старая цена",
			"Остаток",
			"Штрихкод",
			"Состав",
			"Описание",
			"Фото (имена файлов или ссылки)",
		];
		const rows = [
			["LH-PL-01", "Платье «Ривьера» льняное миди", "Лён и Хлопок", "Женщинам / Одежда / Платья", "Молочный", "44", "44", "6490", "7490", "5", "4600000000017", "Лён 100%", "Свободное платье миди с карманами.", "riviera-1.jpg; riviera-2.jpg"],
			["LH-PL-01", "Платье «Ривьера» льняное миди", "Лён и Хлопок", "Женщинам / Одежда / Платья", "Молочный", "46", "46", "6490", "7490", "3", "4600000000024", "", "", ""],
		];
		const csv = [header, ...rows].map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(";")).join("\r\n");
		return { blob: new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" }), filename: "Шаблон загрузки каталога OSMIX.csv" };
	},

	models: async () => {
		await sleep(150);
		return { models: structuredClone(MODELS), default: DEFAULT_MODEL, enabled: true };
	},
};
